/**
 * DraggrManagerPragmatic.ts - Modern Draggr using Pragmatic drag and drop
 * 
 * CHANGE HISTORY:
 * - 251108_1245 danielbeer: feat: Rewrite Draggr to use Pragmatic drag and drop
 * 
 * ARCHITECTURE NOTE:
 * This is a complete rewrite of DraggrManager using Atlassian's Pragmatic drag and drop library.
 * 
 * Why Pragmatic drag and drop:
 * 1. Framework-agnostic (vanilla JS core, like @headless-tree)
 * 2. Smaller bundle size (4.7kB vs dnd-kit's ~10kB)
 * 3. More powerful (handles files, text, external drags)
 * 4. Native browser API (better performance)
 * 5. Battle-tested (powers Trello, Jira, Confluence)
 * 6. Can drag across browser windows
 * 
 * Pattern: Actions-Only (No CRUD)
 * - Drag state is ephemeral and event-driven
 * - All operations route through Actr for logging
 * - No persistence needed for drag operations
 */

import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import {
  dropTargetForExternal,
  monitorForExternal,
} from '@atlaskit/pragmatic-drag-and-drop/external/adapter';

import type { Actr } from '@opcentral/actr';

export interface DraggableConfig {
  type: string;
  data: Record<string, any>;
  canDrag?: boolean | (() => boolean);
  onDragStart?: (data: Record<string, any>) => void;
  onDragEnd?: (data: Record<string, any>) => void;
  getInitialData?: () => Record<string, any>;
}

export interface DroppableConfig {
  accepts: string[];
  canDrop?: (source: Record<string, any>) => boolean;
  onDrop?: (source: Record<string, any>, location?: any) => void;
  onDragOver?: (source: Record<string, any>) => void;
  onDragLeave?: () => void;
}

export interface FileExportConfig {
  source: {
    type: 'rest' | 'graphql' | 'filemaker' | 's3' | 'local' | 'multi';
    [key: string]: any;
  };
  fileName: string;
  mimeType?: string;
  onProgress?: (progress: number) => void;
}

export interface DraggrManagerOptions {
  actr?: Actr | undefined;
  backtrackr?: any;
  enableFileExport?: boolean;
  enableMultiSelect?: boolean;
  enableCrossView?: boolean;
}

/**
 * DraggrManager - Universal drag & drop using Pragmatic drag and drop
 * 
 * Provides a unified interface for all drag & drop operations in OpCentral.
 * Routes all operations through Actr for logging and audit trail.
 */
export class DraggrManager {
  private actr?: Actr;
  private backtrackr?: any;
  private options: DraggrManagerOptions;
  private cleanupFunctions: (() => void)[] = [];
  private selectedItems: Set<string> = new Set();
  private currentDrag: any = null;

  constructor(options: DraggrManagerOptions = {}) {
    this.actr = options.actr;
    this.backtrackr = options.backtrackr;
    this.options = {
      actr: options.actr || undefined,
      backtrackr: options.backtrackr || undefined,
      enableFileExport: options.enableFileExport !== false,
      enableMultiSelect: options.enableMultiSelect !== false,
      enableCrossView: options.enableCrossView !== false,
    };

    // Register Actr actions if available
    if (this.actr) {
      this.registerActions();
    }

    // Setup global monitor for all drag events
    this.setupGlobalMonitor();
  }

  /**
   * Register Draggr actions with Actr
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Drag state is ephemeral, no persistence needed
   * - Actions handle setup, fetch, and export operations
   */
  private registerActions(): void {
    if (!this.actr) return;

    // Register drag-start action
    this.actr.register('draggr:drag-start', {
      handler: async (params: any) => {
        this.currentDrag = params;
        console.log('[Draggr] Drag started:', params);
        return { success: true };
      },
    });

    // Register drag-end action
    this.actr.register('draggr:drag-end', {
      handler: async (_params: any) => {
        this.currentDrag = null;
        return { success: true };
      },
    });

    // Register drop action
    this.actr.register('draggr:drop', {
      handler: async (params: any) => {
        // Record in Backtrackr if available
        if (this.backtrackr && params.recordUndo) {
          await this.backtrackr.record({
            type: 'draggr:drop',
            data: params,
            undo: params.undo,
            redo: params.redo,
          });
        }
        return { success: true, data: params };
      },
    });

    // Register file export action
    this.actr.register('draggr:export-file', {
      handler: async (params: FileExportConfig) => {
        return await this.exportFile(params);
      },
    });
  }

  /**
   * Setup global monitor for all drag events
   * Routes all events through Actr for logging
   */
  private setupGlobalMonitor(): void {
    // Monitor element drags
    const cleanupElements = monitorForElements({
      onDragStart: ({ source }) => {
        if (this.actr) {
          this.actr.execute('draggr:drag-start', {
            type: 'element',
            data: source.data,
          });
        }
      },
      onDrop: ({ source, location }) => {
        if (this.actr) {
          this.actr.execute('draggr:drop', {
            type: 'element',
            source: source.data,
            location,
          });
        }
      },
      // Note: Pragmatic D&D doesn't have onDragEnd in monitor
      // Use onDrop instead
    });

    // Monitor external drags (files, text)
    const cleanupExternal = monitorForExternal({
      onDragStart: () => {
        if (this.actr) {
          this.actr.execute('draggr:drag-start', {
            type: 'external',
          });
        }
      },
      onDrop: ({ source, location }) => {
        if (this.actr) {
          this.actr.execute('draggr:drop', {
            type: 'external',
            source,
            location,
          });
        }
      },
    });

    this.cleanupFunctions.push(cleanupElements, cleanupExternal);
  }

  /**
   * Make an element draggable
   * 
   * @param element - HTML element to make draggable
   * @param config - Draggable configuration
   * @returns Cleanup function
   */
  makeDraggable(element: HTMLElement, config: DraggableConfig): () => void {
    const cleanup = draggable({
      element,
      getInitialData: () => ({
        type: config.type,
        ...config.data,
        ...(config.getInitialData?.() || {}),
      }),
      canDrag: () => {
        if (typeof config.canDrag === 'function') {
          return config.canDrag();
        }
        return config.canDrag !== false;
      },
      onDragStart: () => {
        config.onDragStart?.(config.data);
      },
      onDrop: () => {
        config.onDragEnd?.(config.data);
      },
    });

    this.cleanupFunctions.push(cleanup);
    return cleanup;
  }

  /**
   * Make an element a drop target
   * 
   * @param element - HTML element to make droppable
   * @param config - Droppable configuration
   * @returns Cleanup function
   */
  makeDroppable(element: HTMLElement, config: DroppableConfig): () => void {
    const cleanup = dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        // Check if source type is accepted
        const sourceType = source.data.type as string;
        if (!config.accepts.includes(sourceType) && !config.accepts.includes('*')) {
          return false;
        }

        // Call custom canDrop if provided
        if (config.canDrop) {
          return config.canDrop(source.data);
        }

        return true;
      },
      onDrop: ({ source, location }) => {
        config.onDrop?.(source.data, location);
      },
      onDragEnter: ({ source }) => {
        config.onDragOver?.(source.data);
      },
      onDragLeave: () => {
        config.onDragLeave?.();
      },
    });

    this.cleanupFunctions.push(cleanup);
    return cleanup;
  }

  /**
   * Make an element accept external drops (files, text)
   * 
   * @param element - HTML element to make droppable
   * @param onDrop - Drop handler
   * @returns Cleanup function
   */
  makeExternalDroppable(
    element: HTMLElement,
    onDrop: (items: any[]) => void
  ): () => void {
    const cleanup = dropTargetForExternal({
      element,
      onDrop: ({ source }) => {
        onDrop(source.items);
      },
    });

    this.cleanupFunctions.push(cleanup);
    return cleanup;
  }

  /**
   * Export a file (drag-to-desktop or programmatic download)
   * 
   * @param config - File export configuration
   * @returns Promise with file blob
   */
  async exportFile(_config: FileExportConfig): Promise<Blob> {
    // TODO: Implement file export with multi-source support
    // This would integrate with FileSourceManager from original Draggr
    throw new Error('File export not yet implemented in Pragmatic version');
  }

  /**
   * Select an item (for multi-select)
   * 
   * @param itemId - Item ID to select
   * @param addToSelection - Whether to add to existing selection
   */
  selectItem(itemId: string, addToSelection = false): void {
    if (!this.options.enableMultiSelect) {
      this.selectedItems.clear();
      this.selectedItems.add(itemId);
      return;
    }

    if (addToSelection) {
      if (this.selectedItems.has(itemId)) {
        this.selectedItems.delete(itemId);
      } else {
        this.selectedItems.add(itemId);
      }
    } else {
      this.selectedItems.clear();
      this.selectedItems.add(itemId);
    }
  }

  /**
   * Get currently selected items
   * 
   * @returns Set of selected item IDs
   */
  getSelectedItems(): Set<string> {
    return new Set(this.selectedItems);
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.selectedItems.clear();
  }

  /**
   * Get current drag state
   * 
   * @returns Current drag data or null
   */
  getCurrentDrag(): any {
    return this.currentDrag;
  }

  /**
   * Cleanup all drag & drop handlers
   */
  cleanup(): void {
    this.cleanupFunctions.forEach((cleanup) => cleanup());
    this.cleanupFunctions = [];
    this.selectedItems.clear();
    this.currentDrag = null;

    // Unregister Actr actions
    if (this.actr) {
      this.actr.unregister('draggr:drag-start');
      this.actr.unregister('draggr:drag-end');
      this.actr.unregister('draggr:drop');
      this.actr.unregister('draggr:export-file');
    }
  }
}
